// src/components/resources/tools/generators/EmailThread/OutlookMessageRow.tsx

'use client'

import { memo } from 'react'
import { clsx } from 'clsx'
import { getAvatarStyle } from './email-thread-utils'
import type { OutlookMessageRowProps } from './types'

/**
 * A single Outlook-style message row — `React.memo`'d for the same
 * per-row re-render isolation as `GmailMessageRow`. Outlook rows are
 * purely presentational (no per-message controls), so they take no
 * callbacks.
 */
export const OutlookMessageRow = memo(function OutlookMessageRow({
  message,
  isLast,
  showAvatars,
  highlightReply,
}: OutlookMessageRowProps): JSX.Element {
  return (
    <div>
      <div className="flex items-start gap-3">
        {showAvatars && (
          <div
            className={clsx(
              "h-10 w-10 flex-shrink-0 rounded-full flex items-center justify-center text-sm font-medium border",
              getAvatarStyle('outlook', message.isCurrentUser),
              message.isCurrentUser
                ? "border-blue-800"
                : "border-blue-200"
            )}
            title={message.isCurrentUser ? "You" : message.sender}
            aria-label={message.isCurrentUser ? "Your avatar" : `${message.sender} avatar`}
          >
            {message.sender.charAt(0)}
          </div>
        )}
        <div className={clsx(
          "flex-1",
          highlightReply && message.isCurrentUser ? "bg-blue-50 dark:bg-blue-900/30 rounded-lg px-3 py-2" : ""
        )}>
          <div className="flex items-baseline justify-between">
            <div>
              <div className="font-semibold text-sm text-gray-900 dark:text-gray-100">{message.sender}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">{message.email}</div>
              {message.isCurrentUser && (
                <span className="ml-2 px-2 py-0.5 rounded bg-blue-100 text-blue-700 text-xs font-semibold">You</span>
              )}
            </div>
            <span className="text-xs text-gray-500 dark:text-gray-400">{message.timestamp}</span>
          </div>
          <div className="mt-3 text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
            {message.content}
          </div>
        </div>
      </div>
      {!isLast && (
        <div className="my-4 border-b border-gray-200 dark:border-gray-700" aria-hidden="true" />
      )}
    </div>
  )
})
